import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import type { IngredientLine, RecipeDetail as RecipeDetailType, RecipeVersion } from "../lib/types";

const RATIOS = [0.5, 1, 2, 4];

export function RecipeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState<RecipeDetailType | null>(null);
  const [version, setVersion] = useState<RecipeVersion | null>(null);
  const [history, setHistory] = useState<{ version_number: number; changelog: string; created_at: string }[]>([]);
  const [ratio, setRatio] = useState(1);
  const [scaled, setScaled] = useState<{ ingredients: IngredientLine[]; computed: any } | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    const res = await api.get<{ recipe: RecipeDetailType; version: RecipeVersion; versionHistory: any[] }>(`/recipes/${id}`);
    setRecipe(res.recipe);
    setVersion(res.version);
    setHistory(res.versionHistory);
    setRatio(1);
    setScaled(null);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function changeRatio(r: number) {
    setRatio(r);
    if (r === 1) return setScaled(null);
    const res = await api.get<{ ingredients: IngredientLine[]; computed: any }>(`/recipes/${id}/scale?ratio=${r}`);
    setScaled(res);
  }

  async function brew() {
    setBusy(true);
    try {
      const { batchId } = await api.post<{ batchId: string }>("/batches", { recipeId: id, ratio });
      navigate(`/batches?open=${batchId}`);
    } finally {
      setBusy(false);
    }
  }

  async function fork() {
    setBusy(true);
    try {
      const { recipeId } = await api.post<{ recipeId: string }>(`/recipes/${id}/fork`);
      navigate(`/recipes/${recipeId}/edit`);
    } finally {
      setBusy(false);
    }
  }

  async function toggleSave() {
    if (!recipe) return;
    if (recipe.isSaved) await api.delete(`/recipes/${id}/save`);
    else await api.post(`/recipes/${id}/save`);
    setRecipe({ ...recipe, isSaved: !recipe.isSaved });
  }

  if (!recipe || !version) return <main className="page">Loading…</main>;

  const ingredients = scaled?.ingredients ?? version.ingredients;
  const computed = scaled?.computed ?? version.computed;
  const stockless = ingredients; // "have it?" needs pantry cross-reference — kept simple here

  return (
    <main style={{ padding: "0 0 90px" }}>
      <section
        style={{
          padding: "38px 40px 32px",
          display: "grid",
          gridTemplateColumns: "minmax(0,1fr) 300px",
          gap: 56,
          alignItems: "end",
          borderBottom: "2px solid var(--color-divider)",
        }}
      >
        <div>
          <div className="flex-row" style={{ marginBottom: 14 }}>
            <span style={{ width: 10, height: 10, background: "var(--color-accent)" }} />
            <span className="eyebrow">
              {recipe.method} · v{version.versionNumber} · forked {recipe.forkCount} times
            </span>
          </div>
          <h1 style={{ margin: 0, fontSize: 62, lineHeight: 1.02, letterSpacing: "-0.03em", marginLeft: "-0.058em" }}>{recipe.title}</h1>
          <p className="muted" style={{ margin: "22px 0 0", maxWidth: "56ch", fontSize: 16, lineHeight: 1.65 }}>
            {recipe.blurb}
          </p>
          {recipe.forkedFrom && (
            <p className="muted-strong" style={{ fontSize: 13, marginTop: 12 }}>
              Forked from{" "}
              <a href={`/recipes/${recipe.forkedFrom.id}`} onClick={(e) => { e.preventDefault(); navigate(`/recipes/${recipe.forkedFrom!.id}`); }}>
                {recipe.forkedFrom.title}
              </a>
            </p>
          )}
        </div>
        <div className="flex-col" style={{ gap: 8 }}>
          <button className="btn btn-primary btn-block" disabled={busy} onClick={brew}>
            Brew this — start a batch
          </button>
          {!recipe.isOwner && (
            <button className="btn btn-secondary btn-block" disabled={busy} onClick={fork}>
              Fork &amp; edit
            </button>
          )}
          {recipe.isOwner && (
            <button className="btn btn-secondary btn-block" onClick={() => navigate(`/recipes/${id}/edit`)}>
              Edit this recipe
            </button>
          )}
          <button className="btn btn-secondary btn-block" onClick={toggleSave}>
            {recipe.isSaved ? "Saved to shelf ✓" : "Save to shelf"}
          </button>
          <p className="muted-strong" style={{ margin: "12px 0 0", fontSize: 12 }}>
            By {recipe.ownerName.replace(/\.$/, "")}. Last touched {new Date(recipe.updatedAt).toLocaleDateString()}.
          </p>
        </div>
      </section>

      <section style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 2, background: "var(--color-divider)", borderBottom: "2px solid var(--color-divider)" }}>
        <StatCell value={`${computed.abvPercent?.toFixed?.(1) ?? computed.abvPercent}%`} label="ABV, computed" accent />
        <StatCell value={formatL(computed.yieldMl)} label="Yield at this scale" />
        <StatCell value={String(computed.days ?? version.computed.days)} label="Days, start to glass" />
        {computed.originalGravity != null ? (
          <StatCell value={`${computed.originalGravity.toFixed(3)} → ${computed.finalGravity?.toFixed(3)}`} label="OG → FG, est." />
        ) : (
          <StatCell value={String(Math.round(computed.sugarGPerL ?? version.computed.sugarGPerL))} label="g/L sugar" />
        )}
        <StatCell value={String(version.steps.length)} label="Hands-on steps" />
      </section>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 360px", gap: 0, alignItems: "start" }}>
        <section style={{ padding: "36px 40px 60px", minWidth: 0, borderRight: "2px solid var(--color-divider)" }}>
          <div className="spread" style={{ borderBottom: "2px solid var(--color-divider)", paddingBottom: 14 }}>
            <h2 style={{ margin: 0, fontSize: 24, letterSpacing: "-0.018em" }}>What goes in</h2>
            <div className="flex-row">
              <span className="muted-strong" style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Scale
              </span>
              <div className="seg">
                {RATIOS.map((r) => (
                  <label key={r} className="seg-opt">
                    <input type="radio" name="scale" checked={ratio === r} onChange={() => changeRatio(r)} />×{r}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <table className="table" style={{ marginTop: 0 }}>
            <thead>
              <tr>
                <th style={{ width: "44%" }}>Ingredient</th>
                <th>Amount</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {stockless.map((i) => (
                <tr key={i.id}>
                  <td style={{ fontSize: 14.5 }}>{i.name}</td>
                  <td className="tabular" style={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 14, whiteSpace: "nowrap" }}>
                    {i.amountValue} {i.amountUnit}
                  </td>
                  <td className="muted" style={{ fontSize: 13 }}>
                    {i.note}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2 style={{ margin: "44px 0 0", fontSize: 24, letterSpacing: "-0.018em", borderBottom: "2px solid var(--color-divider)", paddingBottom: 14 }}>
            How it goes
          </h2>
          {version.steps.map((s) => (
            <div key={s.id} style={{ display: "grid", gridTemplateColumns: "52px minmax(0,1fr) 130px", gap: 22, padding: "20px 0", borderBottom: "1px solid var(--color-divider)", alignItems: "baseline" }}>
              <span className="tabular" style={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 13 }}>
                {String(s.stepNumber).padStart(2, "0")}
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 18, letterSpacing: "-0.012em" }}>{s.title}</span>
                <span className="muted" style={{ display: "block", marginTop: 7, fontSize: 14, lineHeight: 1.6 }}>
                  {s.body}
                </span>
              </span>
              <span className="eyebrow" style={{ textAlign: "right" }}>
                {s.whenLabel}
              </span>
            </div>
          ))}
        </section>

        <aside style={{ padding: "36px 32px 60px", display: "flex", flexDirection: "column", gap: 32 }}>
          {recipe.sensory && (
            <div>
              <h6 className="eyebrow" style={{ marginBottom: 14 }}>
                How it drinks
              </h6>
              <div className="flex-col" style={{ gap: 12 }}>
                {(["bitter", "sweet", "citrus", "herbal", "body"] as const).map((k) => (
                  <div key={k}>
                    <div className="spread" style={{ fontSize: 12.5, marginBottom: 5 }}>
                      <span style={{ textTransform: "capitalize" }}>{k}</span>
                      <span className="muted-strong tabular">{Math.round(recipe.sensory![k])}</span>
                    </div>
                    <div style={{ height: 6, background: "var(--color-neutral-300)", position: "relative" }}>
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          width: `${recipe.sensory![k]}%`,
                          background: recipe.sensory![k] > 66 ? "var(--color-accent)" : "var(--color-text)",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="muted" style={{ margin: "16px 0 0", fontSize: 12.5, lineHeight: 1.55 }}>
                Averaged from {recipe.sensory.count} tasting note{recipe.sensory.count === 1 ? "" : "s"}.
              </p>
            </div>
          )}

          <div style={{ borderTop: "2px solid var(--color-divider)", paddingTop: 20 }}>
            <h6 className="eyebrow" style={{ marginBottom: 14 }}>
              Version history
            </h6>
            <div className="flex-col">
              {history.map((v) => (
                <div key={v.version_number} style={{ padding: "10px 0", borderBottom: "1px solid var(--color-divider)" }}>
                  <p style={{ margin: 0, fontSize: 13.5 }}>
                    <strong style={{ fontFamily: "var(--font-heading)", fontWeight: 800 }}>v{v.version_number}</strong> — {v.changelog}{" "}
                    <span className="muted-strong">{new Date(v.created_at).toLocaleDateString()}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

function StatCell({ value, label, accent }: { value: string; label: string; accent?: boolean }) {
  return (
    <div style={{ background: "var(--color-bg)", padding: "20px 24px" }}>
      <p
        className="tabular"
        style={{
          margin: 0,
          fontFamily: "var(--font-heading)",
          fontWeight: 800,
          fontSize: 34,
          lineHeight: 1.05,
          marginLeft: "-0.045em",
          color: accent ? "var(--color-accent)" : undefined,
        }}
      >
        {value}
      </p>
      <p className="muted" style={{ margin: "8px 0 0", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {label}
      </p>
    </div>
  );
}

function formatL(ml: number): string {
  if (ml >= 1000) return `${(ml / 1000).toFixed(2).replace(/0$/, "").replace(/\.$/, "")} L`;
  return `${Math.round(ml)} ml`;
}
